/**
 * Sign in / sign up, and the organizer dashboard.
 *
 * Three account shapes share the one form: clubs and societies (organizer),
 * sponsors (advertiser), and platform staff (seeded, never self-registered).
 * Public viewers never sign in at all.
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorNote, Section, Spinner } from '../components/Shell.tsx';
import { getToken, post, setToken } from '../lib/api.ts';
import { useFetch } from '../lib/useLive.ts';

const inputClass =
  'w-full rounded-lg border border-[color-mix(in_oklab,var(--color-fairway-400)_30%,transparent)] bg-[var(--color-fairway-800)] px-3 py-2.5 text-sm text-[var(--color-chalk)] placeholder:text-[var(--color-muted-dim)]';

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'signin' | 'signup'>(params.get('intent') === 'signup' ? 'signup' : 'signin');
  const [accountType, setAccountType] = useState<'organizer' | 'advertiser'>(
    params.get('type') === 'advertiser' || params.get('intent') === 'sponsor' ? 'advertiser' : 'organizer',
  );
  const [form, setForm] = useState({ fullName: '', email: '', password: '', organizationName: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'signup') {
        const result = await post<{ token: string; accountType: string }>('/api/auth/register', {
          fullName: form.fullName,
          email: form.email,
          password: form.password,
          organizationName: form.organizationName,
          accountType,
        });
        setToken(result.token);
        navigate(accountType === 'advertiser' ? '/sponsor' : '/app');
      } else {
        const result = await post<{ token: string }>('/api/auth/login', { email: form.email, password: form.password });
        setToken(result.token);
        navigate(params.get('intent') === 'sponsor' ? '/sponsor' : '/app');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section eyebrow={mode === 'signup' ? 'Create an account' : 'Sign in'} title={mode === 'signup' ? 'Get your first event on the board' : 'Welcome back'}>
      <div className="grid gap-8 lg:grid-cols-[420px_minmax(0,1fr)]">
        <form onSubmit={submit} className="card p-6">
          {mode === 'signup' ? (
            <div className="mb-4 inline-flex rounded-lg border border-[color-mix(in_oklab,var(--color-fairway-400)_28%,transparent)] p-1">
              {(['organizer', 'advertiser'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setAccountType(type)}
                  className={`rounded px-3 py-1.5 text-[12px] font-semibold ${
                    accountType === type ? 'bg-[var(--color-fairway-300)] text-[var(--color-fairway-950)]' : 'text-[var(--color-muted)]'
                  }`}
                >
                  {type === 'organizer' ? 'Club / organiser' : 'Sponsor'}
                </button>
              ))}
            </div>
          ) : null}

          <div className="space-y-3">
            {mode === 'signup' ? (
              <>
                <label className="block">
                  <span className="mb-1 block text-[11px] uppercase tracking-wider text-[var(--color-muted)]">Your name</span>
                  <input required value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} className={inputClass} />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] uppercase tracking-wider text-[var(--color-muted)]">
                    {accountType === 'advertiser' ? 'Company name' : 'Club or society name'}
                  </span>
                  <input
                    required
                    value={form.organizationName}
                    onChange={(e) => setForm({ ...form, organizationName: e.target.value })}
                    className={inputClass}
                  />
                </label>
              </>
            ) : null}
            <label className="block">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-[var(--color-muted)]">Email</span>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-[var(--color-muted)]">
                Password {mode === 'signup' ? <span className="text-[var(--color-muted-dim)]">(10 characters or more)</span> : null}
              </span>
              <input
                required
                type="password"
                minLength={mode === 'signup' ? 10 : 1}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className={inputClass}
              />
            </label>
          </div>

          {error ? (
            <div className="mt-4">
              <ErrorNote message={error} hint={error.includes('database') ? 'Set DATABASE_URL and run npm run db:push && npm run db:seed.' : undefined} />
            </div>
          ) : null}

          <button type="submit" disabled={busy} className="btn btn-primary mt-5 w-full !py-3">
            {busy ? 'Working…' : mode === 'signup' ? 'Create account' : 'Sign in'}
          </button>

          <button
            type="button"
            onClick={() => setMode((m) => (m === 'signup' ? 'signin' : 'signup'))}
            className="mt-3 w-full text-center text-xs text-[var(--color-muted)] underline"
          >
            {mode === 'signup' ? 'Already have an account? Sign in' : 'Need an account? Create one'}
          </button>
        </form>

        <div className="space-y-4">
          <div className="card p-5">
            <h3 className="board-title mb-2 text-lg font-bold">Seeded demo logins</h3>
            <p className="text-[13px] text-[var(--color-muted)]">
              After <code className="text-[var(--color-fairway-300)]">npm run db:seed</code>, these exist on a database with the
              example club and sponsor:
            </p>
            <ul className="mt-3 space-y-2 text-[13px]">
              <li className="rounded-lg bg-[var(--color-fairway-800)] p-3">
                <p className="font-semibold">Club organiser</p>
                <p className="tabular text-[var(--color-muted)]">organizer@aloevalley.example / fairway-demo-2026</p>
              </li>
              <li className="rounded-lg bg-[var(--color-fairway-800)] p-3">
                <p className="font-semibold">Sponsor manager</p>
                <p className="tabular text-[var(--color-muted)]">sponsor@kalaharimotors.example / fairway-demo-2026</p>
              </li>
            </ul>
          </div>
          <div className="card p-5">
            <h3 className="board-title mb-2 text-lg font-bold">No account needed to look</h3>
            <p className="text-[13px] text-[var(--color-muted)]">
              The demo tournament, clubhouse display and rate card are all public.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link to="/demo" className="btn btn-ghost !py-1.5 !text-xs">
                Live demo
              </Link>
              <Link to="/rate-card" className="btn btn-ghost !py-1.5 !text-xs">
                Rate card
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

interface Me {
  user: { id: string; email: string };
  organizations: Array<{
    organizationId: string;
    name: string;
    slug: string;
    type: string;
    role: string;
    plan: { key: string; name: string; status: string };
    entitlements: Record<string, boolean | number | string>;
  }>;
}

/** Organizer dashboard: events, plan usage, ad-zone control and settlement. */
export function OrganizerDashboard() {
  const token = getToken();
  const me = useFetch<Me>(token ? '/api/auth/me' : null);
  const navigate = useNavigate();

  if (!token) {
    return (
      <Section title="Organiser dashboard">
        <div className="card max-w-[520px] p-6">
          <p className="text-sm text-[var(--color-muted)]">Sign in to manage your events, boards and sponsors.</p>
          <Link to="/login" className="btn btn-primary mt-4">
            Sign in
          </Link>
        </div>
      </Section>
    );
  }

  if (me.loading) return <Spinner label="Loading your clubs" />;
  if (me.error || !me.data) {
    return (
      <Section title="Organiser dashboard">
        <ErrorNote message={me.error ?? 'Could not load your account'} />
      </Section>
    );
  }

  const org = me.data.organizations.find((o) => o.type !== 'advertiser') ?? me.data.organizations[0];
  if (!org) {
    return (
      <Section title="Organiser dashboard">
        <ErrorNote message="No organization on this login." />
      </Section>
    );
  }

  return (
    <div>
      <Section eyebrow={`${org.name} · ${org.plan.name} plan`} title="Your events">
        <div className="mb-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => {
              setToken(null);
              navigate('/');
            }}
            className="btn btn-ghost !py-1.5 !text-xs"
          >
            Sign out
          </button>
          <Link to="/app/billing" className="btn btn-ghost !py-1.5 !text-xs">
            Plan and billing
          </Link>
          <Link to="/score" className="btn btn-ghost !py-1.5 !text-xs">
            Open the scorecard
          </Link>
        </div>
        <EventsList orgId={org.organizationId} entitlements={org.entitlements} />
      </Section>
    </div>
  );
}

function EventsList({ orgId, entitlements }: { orgId: string; entitlements: Record<string, boolean | number | string> }) {
  const events = useFetch<{
    tournaments: Array<{ id: string; slug: string; name: string; status: string; startsOn: string | null; currentRound: number; roundCount: number }>;
    limits: { activeEvents: number | string; playersPerEvent: number; leaderboardsPerEvent: number };
  }>(`/api/orgs/${orgId}/tournaments`);

  if (events.loading) return <Spinner label="Loading events" />;
  if (events.error) return <ErrorNote message={events.error} />;

  return (
    <>
      <div className="mb-5 grid gap-3 sm:grid-cols-4">
        <LimitCard label="Active events" value={String(events.data?.limits.activeEvents ?? '—')} />
        <LimitCard label="Players per event" value={String(events.data?.limits.playersPerEvent ?? '—')} />
        <LimitCard label="Boards per event" value={String(events.data?.limits.leaderboardsPerEvent ?? '—')} />
        <LimitCard
          label="Ad revenue share"
          value={`${entitlements['ads.revenue_share_percent'] ?? 0}%`}
          note={entitlements['ads.suppress_interstitial'] ? 'Pop-ups removed' : 'Pop-ups on'}
        />
      </div>

      {!events.data?.tournaments.length ? (
        <div className="card p-6">
          <p className="text-sm text-[var(--color-muted)]">
            No events yet. Create one through the API (<code>POST /api/orgs/{orgId}/tournaments</code>) or the seeded example
            event, then it appears here with its boards.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {events.data.tournaments.map((event) => (
            <div key={event.id} className="card flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <p className="font-semibold">{event.name}</p>
                <p className="text-[11px] text-[var(--color-muted-dim)]">
                  {event.startsOn ?? 'no date'} · round {event.currentRound}/{event.roundCount} ·{' '}
                  <span className="uppercase">{event.status.replace(/_/g, ' ')}</span>
                </p>
              </div>
              <div className="flex gap-2">
                <Link to={`/t/${event.slug}`} className="btn btn-ghost !py-1.5 !text-xs">
                  Board
                </Link>
                <Link to={`/t/${event.slug}/bigscreen`} className="btn btn-ghost !py-1.5 !text-xs">
                  Big screen
                </Link>
                <Link to={`/score/${event.id}?org=${orgId}`} className="btn btn-primary !py-1.5 !text-xs">
                  Score
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function LimitCard({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="card p-4">
      <p className="eyebrow">{label}</p>
      <p className="tabular mt-1 text-xl font-bold text-[var(--color-fairway-300)]">{value}</p>
      {note ? <p className="text-[11px] text-[var(--color-muted-dim)]">{note}</p> : null}
    </div>
  );
}

/** Plan, entitlements and invoice history, with in-place upgrade. */
export function BillingPage() {
  const token = getToken();
  const me = useFetch<Me>(token ? '/api/auth/me' : null);
  const org = me.data?.organizations.find((o) => o.type !== 'advertiser') ?? me.data?.organizations[0];
  const billing = useFetch<{
    plan: { key: string; name: string; status: string };
    entitlements: Array<{ key: string; label: string; group: string; value: unknown; source: string }>;
    availablePlans: Array<{ key: string; name: string; priceMonthlyCents: number | null; priceDisplay: string; badge: string | null }>;
    availableAddOns: Array<{ key: string; name: string; description: string; priceDisplay: string }>;
    payments: Array<{ id: string; purpose: string; status: string; amountDisplay: string; createdAt: string }>;
    provider: { key: string; live: boolean; label: string };
  }>(org ? `/api/orgs/${org.organizationId}/billing` : null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <Section title="Billing">
        <Link to="/login" className="btn btn-primary">
          Sign in
        </Link>
      </Section>
    );
  }
  if (billing.loading || me.loading) return <Spinner label="Loading billing" />;
  if (!org || billing.error || !billing.data) {
    return (
      <Section title="Billing">
        <ErrorNote message={billing.error ?? 'Billing unavailable'} />
      </Section>
    );
  }

  const subscribe = async (planKey: string) => {
    setBusy(planKey);
    setError(null);
    try {
      const result = await post<{ payment: { redirectUrl: string } | null; message?: string }>(
        `/api/orgs/${org.organizationId}/billing/subscribe`,
        { planKey, billingPeriod: 'monthly' },
      );
      if (result.payment?.redirectUrl) window.location.href = result.payment.redirectUrl;
      else billing.reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <Section eyebrow={`${org.name}`} title={`${billing.data.plan.name} · ${billing.data.plan.status}`}>
        {error ? (
          <div className="mb-5">
            <ErrorNote message={error} />
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {billing.data.availablePlans.map((plan) => (
            <div key={plan.key} className={`card p-5 ${plan.key === billing.data?.plan.key ? 'border-[var(--color-fairway-300)]' : ''}`}>
              <h3 className="board-title text-lg font-bold">{plan.name}</h3>
              <p className="tabular mt-1 text-xl font-bold">{plan.priceDisplay}</p>
              {plan.key === billing.data?.plan.key ? (
                <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-fairway-300)]">Current plan</p>
              ) : (
                <button
                  type="button"
                  onClick={() => void subscribe(plan.key)}
                  disabled={busy === plan.key || plan.priceMonthlyCents === null}
                  className="btn btn-ghost mt-3 w-full !py-1.5 !text-xs"
                >
                  {plan.priceMonthlyCents === null ? 'Contact sales' : busy === plan.key ? 'Opening…' : `Switch to ${plan.name}`}
                </button>
              )}
            </div>
          ))}
        </div>

        <p className="mt-4 text-xs text-[var(--color-muted-dim)]">
          Gateway: {billing.data.provider.label}
          {billing.data.provider.live ? '' : ' — no real money can be taken until a live provider is configured.'}
        </p>
      </Section>

      <Section eyebrow="Entitlements" title="What your plan currently allows">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <tbody>
              {billing.data.entitlements.map((entitlement) => (
                <tr key={entitlement.key} className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_10%,transparent)]">
                  <td className="py-2">{entitlement.label}</td>
                  <td className="tabular py-2 text-right font-semibold">
                    {typeof entitlement.value === 'boolean' ? (entitlement.value ? '✓' : '—') : String(entitlement.value ?? '—')}
                  </td>
                  <td className="py-2 pl-4 text-right text-[10px] uppercase tracking-wider text-[var(--color-muted-dim)]">
                    {entitlement.source}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {billing.data.payments.length ? (
        <Section eyebrow="Invoices" title="Payment history">
          <div className="space-y-2">
            {billing.data.payments.map((payment) => (
              <div key={payment.id} className="card flex items-center justify-between p-3 text-sm">
                <span className="capitalize">{payment.purpose.replace(/_/g, ' ')}</span>
                <span className="tabular">{payment.amountDisplay}</span>
                <span className="text-[11px] uppercase tracking-wider text-[var(--color-muted)]">{payment.status}</span>
              </div>
            ))}
          </div>
        </Section>
      ) : null}
    </div>
  );
}
